import { BackButton } from "@/components/booking/BackButton";
import { BackToHomeButton } from "@/components/booking/BackToHomeButton";
import { CancelButton } from "@/components/cancel-button";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { RippleAnimation } from "@/components/map/ripple-animation";
import { NearbyDriverMarker, SearchMap } from "@/components/map/search-map";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors, Colors } from "@/constants/theme";
import { useBooking } from "@/hooks/queries/use-bookings-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { isCustomerRole } from "@/lib/roles";
import bookingService, { Booking } from "@/lib/services/bookings";
import driverService from "@/lib/services/drivers";
import { webSocketService } from "@/lib/services/websocket";
import { showToast } from "@/lib/toast";
import { useAuthStore } from "@/stores/use-auth-store";
import { desktopStyles } from "@/styles/live-booking";
import BottomSheet, { BottomSheetScrollView } from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import {
  router,
  useFocusEffect,
  useLocalSearchParams,
  useSegments,
} from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Animated,
  AppState,
  AppStateStatus,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import MapView from "react-native-maps";
import {
  SafeAreaView
} from "react-native-safe-area-context";

const TOTAL_SECONDS = 60;
const SEARCH_RADIUS_MILES = 30;
const COUNTDOWN_ZOOM_OUT_LEVELS = 3;
const INITIAL_LATITUDE_DELTA = 0.15;
const ZOOM_STEP_MS = 400;

function deltaAtProgress(start: number, end: number, progress: number): number {
  const clamped = Math.min(Math.max(progress, 0), 1);
  return Math.exp(Math.log(start) + (Math.log(end) - Math.log(start)) * clamped);
}

export default function SearchDriverRoute() {
  return (
    <DesktopAppFrame>
      <SearchDriverScreen />
    </DesktopAppFrame>
  );
}

function SearchDriverScreen() {
  const { id } = useLocalSearchParams();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const desktop = useDesktopLayout();
  const segments = useSegments();
  const { user, setActiveBooking } = useAuthStore();
  const bookingId = Number(id);
  const {
    data: booking,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useBooking(bookingId);
  const [fetchingDrivers, setFetchingDrivers] = useState(false);
  const [startCountdown, setStartCountdown] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [nearbyDrivers, setNearbyDrivers] = useState<NearbyDriverMarker[]>([]);
  const [operatorsNotified, setOperatorsNotified] = useState(0);
  const progress = useRef(new Animated.Value(1)).current;
  const mapRef = useRef<MapView>(null);
  const bottomSheetRef = useRef<BottomSheet>(null);
  const appStateRef = useRef(AppState.currentState);
  const countdownZoomLockRef = useRef(false);
  const countdownZoomStartedRef = useRef(false);
  const countdownStartedAtRef = useRef<number | null>(null);
  const zoomStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const zoomSpanRef = useRef<{
    startLatitudeDelta: number;
    startLongitudeDelta: number;
    endLatitudeDelta: number;
    endLongitudeDelta: number;
  } | null>(null);
  const bookingRef = useRef(booking);
  const snapPoints = useMemo(() => ["44%"], []);

  bookingRef.current = booking;

  useFocusEffect(
    useCallback(() => {
      setActiveBooking(bookingId);
    }, [bookingId, setActiveBooking]),
  );

  useEffect(() => {
    const handleAppStateChange = (nextState: AppStateStatus) => {
      if (
        appStateRef.current.match(/inactive|background/) &&
        nextState === "active"
      ) {
        void refetch();
      }
      appStateRef.current = nextState;
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, [refetch]);

  useEffect(() => {
    if (!bookingId) return;

    const unsubscribe = webSocketService.subscribeBookingLive((data) => {
      if (data.type === "new_quote" || data.type === "new_quote_received") {
        if (segments[2] === "quotes") {
          return;
        }
        router.push(`/bookings/${bookingId}/quotes`);
      }
      if (data.booking?.status && data.booking.status === "accepted") {
        router.replace(`/bookings/${bookingId}/live-tracking`);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [bookingId, segments]);

  useEffect(() => {
    // Progress animation
    if (!startCountdown) return;

    Animated.timing(progress, {
      toValue: 0,
      duration: TOTAL_SECONDS * 1000,
      useNativeDriver: false,
    }).start();

    // Countdown timer
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          setStartCountdown(false);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [startCountdown, progress]);

  const beginCountdownZoom = useCallback(async () => {
    const map = mapRef.current;
    if (!map || zoomSpanRef.current) {
      return;
    }

    countdownZoomLockRef.current = true;

    let latitudeDelta = INITIAL_LATITUDE_DELTA;
    let longitudeDelta = INITIAL_LATITUDE_DELTA;

    try {
      const bounds = await map.getMapBoundaries();
      const nextLatitudeDelta = Math.abs(
        bounds.northEast.latitude - bounds.southWest.latitude,
      );
      const nextLongitudeDelta = Math.abs(
        bounds.northEast.longitude - bounds.southWest.longitude,
      );
      if (nextLatitudeDelta > 0 && nextLatitudeDelta < 2) {
        latitudeDelta = nextLatitudeDelta;
      }
      if (nextLongitudeDelta > 0 && nextLongitudeDelta < 2) {
        longitudeDelta = nextLongitudeDelta;
      }
    } catch {
      // Use the span implied by the map's initial region.
    }

    const scale = 2 ** COUNTDOWN_ZOOM_OUT_LEVELS;
    zoomSpanRef.current = {
      startLatitudeDelta: latitudeDelta,
      startLongitudeDelta: longitudeDelta,
      endLatitudeDelta: latitudeDelta * scale,
      endLongitudeDelta: longitudeDelta * scale,
    };
  }, []);

  const fitMapToDrivers = useCallback(
    (bookingData: Booking, drivers: NearbyDriverMarker[]) => {
      if (countdownZoomLockRef.current || !mapRef.current || !bookingData) {
        return;
      }

      const pickup = {
        latitude: Number(bookingData.from.latitude),
        longitude: Number(bookingData.from.longitude),
      };

      const coordinates = [
        pickup,
        ...drivers.map((driver) => ({
          latitude: Number(driver.latitude),
          longitude: Number(driver.longitude),
        })),
      ];

      mapRef.current.fitToCoordinates(coordinates, {
        edgePadding: { top: 100, right: 60, bottom: 320, left: 60 },
        animated: true,
      });
    },
    [],
  );

  const loadNearbyDrivers = useCallback(
    async (bookingData: Booking) => {
      try {
        const response = await driverService.getAvailableDrivers({
          from_latitude: Number(bookingData.from.latitude),
          from_longitude: Number(bookingData.from.longitude),
          to_latitude: bookingData.to?.latitude
            ? Number(bookingData.to.latitude)
            : undefined,
          to_longitude: bookingData.to?.longitude
            ? Number(bookingData.to.longitude)
            : undefined,
          service_type: bookingData.service_type,
        });

        const markers: NearbyDriverMarker[] = response.data
          .filter(
            (driver) =>
              driver.current_location?.latitude &&
              driver.current_location?.longitude,
          )
          .map((driver) => ({
            driver_id: driver.driver_id,
            latitude: Number(driver.current_location.latitude),
            longitude: Number(driver.current_location.longitude),
          }));

        if (markers.length > 0) {
          setNearbyDrivers(markers);
          setOperatorsNotified(markers.length);
          fitMapToDrivers(bookingData, markers);
          setTimeout(() => fitMapToDrivers(bookingData, markers), 300);
          return;
        }

        setNearbyDrivers([]);
        setOperatorsNotified(0);
      } catch (error) {
        console.error("Error loading nearby drivers:", error);
      }
    },
    [fitMapToDrivers],
  );

  useEffect(() => {
    if (!booking) return;

    if (booking.status === "cancelled") {
      setActiveBooking(null);
      router.replace(`/bookings/${bookingId}`);
      return;
    }

    if (booking.status === "pending" || booking.status === "quoted") {
      setStartCountdown(true);
    } else {
      router.replace(`/bookings/${bookingId}/live-tracking`);
    }

    loadNearbyDrivers(booking);
  }, [booking, bookingId, loadNearbyDrivers, setActiveBooking]);

  useEffect(() => {
    if (!isError || !error) return;

    const err = error as {
      response?: { data?: { message?: string }; status?: number };
    };
    console.error("Error loading booking:", error);
    showToast.error(
      err.response?.data?.message || "Failed to load booking details",
    );
    if (err.response?.status === 404 || err.response?.status === 403) {
      router.back();
    }
  }, [isError, error]);

  useEffect(() => {
    if (!startCountdown) {
      countdownZoomLockRef.current = false;
      countdownZoomStartedRef.current = false;
      countdownStartedAtRef.current = null;
      zoomSpanRef.current = null;
      if (zoomStartTimeoutRef.current) {
        clearTimeout(zoomStartTimeoutRef.current);
        zoomStartTimeoutRef.current = null;
      }
      return;
    }

    countdownStartedAtRef.current = Date.now();

    const timer = setInterval(() => {
      const span = zoomSpanRef.current;
      const map = mapRef.current;
      const currentBooking = bookingRef.current;
      const startedAt = countdownStartedAtRef.current;
      if (!span || !map || !currentBooking || startedAt == null) {
        return;
      }

      const latitude = Number(currentBooking.from.latitude);
      const longitude = Number(currentBooking.from.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return;
      }

      const progress = Math.min(
        (Date.now() - startedAt) / (TOTAL_SECONDS * 1000),
        1,
      );

      map.animateToRegion(
        {
          latitude,
          longitude,
          latitudeDelta: deltaAtProgress(
            span.startLatitudeDelta,
            span.endLatitudeDelta,
            progress,
          ),
          longitudeDelta: deltaAtProgress(
            span.startLongitudeDelta,
            span.endLongitudeDelta,
            progress,
          ),
        },
        ZOOM_STEP_MS,
      );
    }, ZOOM_STEP_MS);

    return () => clearInterval(timer);
  }, [startCountdown]);

  const handleMapReady = useCallback(() => {
    if (!booking) {
      return;
    }

    if (startCountdown) {
      if (!countdownZoomLockRef.current) {
        fitMapToDrivers(booking, nearbyDrivers);
      }
      if (countdownZoomStartedRef.current) {
        return;
      }
      countdownZoomStartedRef.current = true;
      zoomStartTimeoutRef.current = setTimeout(() => {
        void beginCountdownZoom();
      }, 800);
      return;
    }

    fitMapToDrivers(booking, nearbyDrivers);
  }, [booking, nearbyDrivers, fitMapToDrivers, startCountdown, beginCountdownZoom]);

  const handleFetchDrivers = async (bookingData?: Booking) => {
    const targetBooking = bookingData || booking;
    if (!targetBooking) return;

    try {
      setFetchingDrivers(true);
      const response = await bookingService.searchNearbyDrivers(
        targetBooking.id,
      );
      const notified =
        response?.meta?.nearby_providers_notified ?? nearbyDrivers.length;
      setOperatorsNotified(notified);
      setStartCountdown(true);
      setSecondsLeft(TOTAL_SECONDS);
      progress.setValue(1);
      showToast.success("Nearby drivers have been notified!");
    } catch (error: any) {
      if (error.response?.status === 429) {
        showToast.warning(
          error.response.data.message || "Please wait before searching again",
        );
      } else if (error.response?.status === 400) {
        showToast.error(
          error.response.data.message || "Sorry the booking is not available",
        );
        router.replace("/bookings");
      } else {
        showToast.error(
          error.response.data.message || "Failed to notify nearby drivers",
        );
      }
    } finally {
      setFetchingDrivers(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.loadingSafeArea}>
        <Loader />
      </SafeAreaView>
    );
  }

  if (
    (booking?.status === "pending" || booking?.status === "quoted") &&
    isCustomerRole(user?.role)
  ) {
    if (!startCountdown && booking) {
      const reachMoreContent = (
        <>
          <View style={styles.sheetHeader}>
            <View style={styles.sheetHeaderIcon}>
              <Ionicons
                name="globe-outline"
                size={22}
                color={BrandColors.primary}
              />
            </View>
            <View style={styles.sheetHeaderText}>
              <ThemedText style={styles.sheetTitle} type="defaultSemiBold">
                Reach more Drivers
              </ThemedText>
              <ThemedText style={styles.sheetSubtitle} type="small">
                Increase your search area or notify more drivers to get faster
                response
              </ThemedText>
            </View>
          </View>

          <View style={styles.statsCard}>
            <View style={styles.statColumn}>
              <Ionicons
                name="calendar-outline"
                size={18}
                color={BrandColors.primary}
              />
              <ThemedText style={styles.statLabel} type="extraSmall">
                Current search results
              </ThemedText>
              <ThemedText style={styles.statValue} type="defaultSemiBold">
                {SEARCH_RADIUS_MILES} miles
              </ThemedText>
            </View>

            <View style={styles.statDivider} />

            <View style={styles.statColumn}>
              <Ionicons
                name="calendar-outline"
                size={18}
                color={BrandColors.primary}
              />
              <ThemedText style={styles.statLabel} type="extraSmall">
                Operators notified
              </ThemedText>
              <ThemedText style={styles.statValue} type="defaultSemiBold">
                {operatorsNotified}
              </ThemedText>
            </View>
          </View>

          <View style={styles.sheetActions}>
            <TouchableOpacity
              disabled={fetchingDrivers}
              style={[
                styles.notifyButton,
                fetchingDrivers && styles.notifyButtonDisabled,
              ]}
              onPress={() => handleFetchDrivers()}
              activeOpacity={0.85}
            >
              {fetchingDrivers ? (
                <ActivityIndicator color={BrandColors.white} />
              ) : (
                <ThemedText
                  style={styles.notifyButtonText}
                  type="smallSemiBold"
                >
                  Notify more Drivers
                </ThemedText>
              )}
            </TouchableOpacity>

            <CancelButton
              id={id as string}
              serviceType={booking.service_type}
              title="Cancel"
              variant="outline"
              showIcon={false}
              isDisabled={fetchingDrivers}
              style={[
                styles.sheetCancelButton,
                { backgroundColor: theme.background },
              ]}
            />
          </View>
        </>
      );

      return (
        <GestureHandlerRootView style={styles.container}>
          <View style={styles.mapContainer}>
            <SearchMap
              pickupLocation={booking.from}
              nearbyDrivers={nearbyDrivers}
              mapRef={mapRef}
              onMapReady={handleMapReady}
              style={styles.map}
            />

            <BackButton />

            {nearbyDrivers.length === 0 ? (
              <View style={styles.noOperatorsBadge}>
                <Ionicons name="close-circle" size={16} color="#F87171" />
                <ThemedText style={styles.noOperatorsText} type="smallSemiBold">
                  No operators available
                </ThemedText>
              </View>
            ) : null}

            <View style={styles.rippleContainer} pointerEvents="none">
              <RippleAnimation size={300} color="rgba(248, 113, 113, 0.35)" />
            </View>
          </View>

          {desktop ? (
            <View
              pointerEvents="box-none"
              style={desktopStyles.desktopPanelWrap}
            >
              <View
                style={[
                  desktopStyles.desktopPanel,
                  {
                    backgroundColor: theme.background,
                    borderColor: theme.border,
                  },
                ]}
              >
                <ScrollView
                  contentContainerStyle={[
                    styles.sheetContent,
                    desktopStyles.desktopPanelContent,
                  ]}
                  showsVerticalScrollIndicator={false}
                >
                  {reachMoreContent}
                </ScrollView>
              </View>
            </View>
          ) : (
            <BottomSheet
              ref={bottomSheetRef}
              index={0}
              snapPoints={snapPoints}
              handleIndicatorStyle={styles.sheetHandle}
              backgroundStyle={[
                styles.sheetBackground,
                { backgroundColor: theme.background },
              ]}
            >
              <BottomSheetScrollView
                contentContainerStyle={styles.sheetContent}
                showsVerticalScrollIndicator={false}
              >
                {reachMoreContent}
              </BottomSheetScrollView>
            </BottomSheet>
          )}
        </GestureHandlerRootView>
      );
    }

    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.mapContainer}>
          <BackButton />
          <SearchMap
            pickupLocation={booking.from}
            nearbyDrivers={nearbyDrivers}
            mapRef={mapRef}
            onMapReady={handleMapReady}
            style={styles.map}
          />
          <View style={styles.rippleContainer} pointerEvents="none">
            <RippleAnimation size={300} color={theme.ripple} />
          </View>

          <View
            style={
              desktop ? desktopStyles.desktopPanelWrap : styles.bottomOverlay
            }
          >
            <CancelButton
              id={id as string}
              serviceType={booking?.service_type}
              style={styles.cancelSearchingButton}
            />
          </View>
        </View>
      </SafeAreaView>
    );
  } else if (booking?.status === "cancelled") {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.mapContainer}>
          <BackToHomeButton />
        </View>
      </SafeAreaView>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#0b1120",
  },
  loadingSafeArea: {
    flex: 1,
    backgroundColor: BrandColors.white,
  },
  mapContainer: {
    flex: 1,
    backgroundColor: "#0b1120",
  },
  map: {
    flex: 1,
  },
  noOperatorsBadge: {
    position: "absolute",
    top: 64,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
    zIndex: 10,
  },
  noOperatorsText: {
    color: BrandColors.white,
  },
  bottomOverlay: {
    position: "absolute",
    bottom: 40,
    left: 20,
    right: 20,
    alignItems: "center",
  },
  cancelSearchingButton: {
    backgroundColor: "#e26a6a",
    borderColor: "#e26a6a",
    height: 56,
    borderRadius: 16,
  },
  rippleContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  sheetBackground: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  sheetHandle: {
    backgroundColor: BrandColors.lightGray,
    width: 48,
  },
  sheetContent: {
    paddingHorizontal: 20,
    paddingBottom: 32,
    gap: 20,
  },
  sheetHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 14,
  },
  sheetHeaderIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: BrandColors.primaryLight,
    alignItems: "center",
    justifyContent: "center",
  },
  sheetHeaderText: {
    flex: 1,
    gap: 6,
  },
  sheetTitle: {
    fontSize: 20,
  },
  sheetSubtitle: {
    color: BrandColors.mediumGray,
    lineHeight: 20,
  },
  statsCard: {
    flexDirection: "row",
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#CFF7F4",
    paddingVertical: 18,
    paddingHorizontal: 12,
  },
  statColumn: {
    flex: 1,
    alignItems: "center",
    gap: 6,
  },
  statDivider: {
    width: 1,
    backgroundColor: "#B8EBE8",
    marginVertical: 4,
  },
  statLabel: {
    color: BrandColors.mediumGray,
    textAlign: "center",
  },
  statValue: {
    fontSize: 18,
    fontWeight: "700",
    color: BrandColors.charcoal,
  },
  sheetActions: {
    flexDirection: "row",
    gap: 12,
    alignItems: "stretch",
  },
  notifyButton: {
    flex: 3,
    backgroundColor: BrandColors.primary,
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
  },
  notifyButtonDisabled: {
    opacity: 0.6,
  },
  notifyButtonText: {
    color: BrandColors.white,
    fontSize: 15,
    fontWeight: "700",
  },
  sheetCancelButton: {
    flex: 1,
    height: 52,
    borderRadius: 14,
  },
});
